/**
 * Google Analytics — configured at build time, loaded only after consent.
 *
 * Two independent gates, and both must open.
 *
 * The first is `VITE_GA_ID`. Read as a static property so Vite
 * substitutes the literal when the bundle is built: with no ID the guard
 * folds to a constant and the tag is dropped from the bundle rather than
 * shipped switched off. A build with no analytics configured provably
 * contains none. The ID is therefore fixed when the image is built — see
 * the Dockerfile's build args.
 *
 * The second is the reader. Nothing is fetched from Google until consent
 * is given: not the script, not a cookieless ping. Consent Mode would
 * allow the tag to load in a denied state and still measure, but "we
 * loaded Google's tracker before you answered" is not what a consent
 * banner appears to promise, and this is a site whose readers are looking
 * up who profits from public money. Declining is remembered, so the
 * question is asked once.
 *
 * Two departures from the stock snippet, both forced by this app:
 *
 * 1. `send_page_view: false`, with views sent by hand. The router is
 *    hash-based, and gtag's automatic view fires once per document load —
 *    left alone it records one view per session and attributes a whole
 *    visit to whatever page the reader entered on.
 *
 * 2. A person's URL is reduced before it is sent. The path carries the
 *    SHA-256 standing in for someone's ЕГН, so a full path would tell
 *    Google which individual a given reader had been studying. The
 *    pseudonym protects the person from us; it must not become a third
 *    party's tracking key. Company and municipality identifiers are
 *    public and go through whole.
 */

const ID = import.meta.env.VITE_GA_ID;

/** Where the reader's answer is kept. Not a cookie: storing the refusal
    in the thing being refused would be its own joke, and localStorage is
    first-party, sent nowhere. */
const CONSENT_KEY = 'tetha.analytics-consent';

export type Consent = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let started = false;

/** Whether analytics exists in this build at all. */
export function analyticsConfigured(): boolean {
  return Boolean(ID);
}

/** The stored answer, or null if the reader has not been asked yet. */
export function storedConsent(): Consent | null {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    // Private browsing, or storage disabled. Treat as unanswered rather
    // than as consent: the failure must not become a yes.
    return null;
  }
}

/** Records the answer and acts on it. Granting starts the tag at once. */
export function setConsent(consent: Consent): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, consent);
  } catch {
    // Not fatal: the choice holds for this page, the question returns later.
  }
  if (consent === 'granted') {
    startAnalytics();
    trackPageView(window.location.hash.replace(/^#/, '') || '/');
  }
}

/** Forgets the answer, so the banner asks again. */
export function clearConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch { /* nothing to forget */ }
}

/** Injects the tag. Only ever called once consent is in hand. */
function startAnalytics(): void {
  if (started || !ID || typeof window === 'undefined') return;
  started = true;

  window.dataLayer = window.dataLayer ?? [];
  // The canonical shape: gtag pushes its own `arguments`, and GA reads
  // the entries positionally. Pushing a rest array instead is a subtle
  // way to have commands silently ignored.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  // Stated explicitly even though the tag only loads on consent: the
  // reader agreed to measurement, not to advertising or profiling, and
  // the defaults should say so rather than rely on none being requested.
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });

  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ID)}`;
  document.head.appendChild(tag);

  window.gtag('js', new Date());
  window.gtag('config', ID, { send_page_view: false, anonymize_ip: true });
}

/** Starts analytics if it is configured and already consented to. */
export function initAnalytics(): void {
  if (ID && storedConsent() === 'granted') startAnalytics();
}

/** Reduces a route to what is safe to report. */
export function reportablePath(path: string): string {
  return path.replace(/^\/people\/[^/]+/, '/people/:person');
}

/** One page view, for a hash route the tag cannot see for itself. */
export function trackPageView(path: string, search = ''): void {
  if (!started || !window.gtag) return;
  const reportable = reportablePath(path);
  const query = search ? `?${search}` : '';
  window.gtag('event', 'page_view', {
    page_path: `${reportable}${query}`,
    page_location: `${window.location.origin}/#${reportable}`,
    page_title: document.title,
  });
}
